import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Database, Loader2, ChevronDown, ExternalLink } from 'lucide-react';
import type { StrategicPhase, CompetitorData } from '@/hooks/useStrategicStream';

function DataCell({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  const colorClass = highlight ? 'text-purple-400' : 'text-text-primary';
  return (
    <div className="bg-surface/50 rounded-lg p-2">
      <span className="text-text-muted text-xs">{label}</span>
      <p className={cn('font-mono text-sm', colorClass)}>{value}</p>
    </div>
  );
}

function CompetitorDataCell({ label, value, colorClass }: { label: string; value: string; colorClass?: string }) {
  return (
    <div className="bg-surface/50 rounded-lg p-2.5">
      <span className="text-text-secondary text-xs block mb-0.5">{label}</span>
      <p className={cn('font-mono text-sm break-words leading-snug', colorClass || 'text-text-primary')} title={value}>{value}</p>
    </div>
  );
}

export function DataAnchorNode({ phase, workstreamCount, competitorData, isSmallScreen }: {
  phase: StrategicPhase;
  workstreamCount: number;
  competitorData: CompetitorData[];
  isSmallScreen: boolean;
}) {
  const [collapsedTickers, setCollapsedTickers] = useState<Set<string>>(new Set());
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const isActive = phase === 'data_anchoring';
  const isComplete = ['workstreams', 'synthesis', 'stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase);
  const hasCompetitors = competitorData && competitorData.length > 0;

  // Auto-collapse when phase progresses past data anchoring to reduce scroll fatigue
  useEffect(() => {
    if (isComplete && phase !== 'data_anchoring' && phase !== 'workstreams' && hasCompetitors) {
      setSectionCollapsed(true);
    }
  }, [isComplete, phase, hasCompetitors]);

  // Compact summary when collapsed
  const tickerSummary = hasCompetitors
    ? competitorData.map(c => c.ticker).join(', ')
    : '';

  return (
    <div
      className={cn(
        'relative border transition-all duration-500 mx-auto',
        'backdrop-blur-md bg-gradient-to-br rounded-xl',
        isSmallScreen ? 'max-w-[320px] p-3' : 'max-w-[700px] p-4',
        isActive
          ? 'from-purple-500/15 to-purple-600/5 border-purple-500/40 shadow-lg shadow-purple-500/10'
          : isComplete
          ? 'from-purple-500/10 to-purple-600/5 border-purple-500/30'
          : 'from-card-idle-from/60 to-card-idle-to/40 border-border/50'
      )}
    >
      {/* Header */}
      <button
        onClick={() => hasCompetitors && isComplete && setSectionCollapsed(!sectionCollapsed)}
        className={cn('w-full flex items-center gap-3 mb-3', hasCompetitors && isComplete && 'cursor-pointer')}
      >
        <div
          className={cn(
            'rounded-lg flex items-center justify-center',
            isSmallScreen ? 'w-8 h-8' : 'w-10 h-10',
            isActive ? 'bg-purple-500/25' : isComplete ? 'bg-purple-500/20' : 'bg-surface-light/80'
          )}
        >
          {isActive ? (
            <Loader2 className={cn('animate-spin text-purple-400', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
          ) : (
            <Database className={cn(isComplete ? 'text-purple-400' : 'text-text-muted', isSmallScreen ? 'w-4 h-4' : 'w-5 h-5')} />
          )}
        </div>
        <div className="flex-1 text-left">
          <div className={cn('font-mono uppercase tracking-wider text-purple-400', isSmallScreen ? 'text-xs' : 'text-sm')}>
            DATA ANCHOR
          </div>
          <p className="text-xs text-text-muted">
            {sectionCollapsed && hasCompetitors
              ? `${competitorData.length} companies fetched (${tickerSummary})`
              : hasCompetitors
                ? `FMP Market Data for ${competitorData.length} Companies`
                : 'Industry & Market Intelligence'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn('px-2 py-1 rounded text-xs', isComplete ? 'bg-purple-500/20 text-purple-400' : 'bg-surface-light text-text-muted')}>
            {isActive ? 'Fetching...' : isComplete ? 'Ready' : 'Waiting'}
          </div>
          {hasCompetitors && isComplete && (
            <ChevronDown className={cn('w-4 h-4 text-text-muted transition-transform', !sectionCollapsed && 'rotate-180')} />
          )}
        </div>
      </button>

      {/* Competitor Data Cards - collapsible */}
      {hasCompetitors && isComplete && !sectionCollapsed && (
        <div className="space-y-2 mb-3">
          {competitorData.map((competitor) => (
            <div
              key={competitor.ticker}
              className={cn(
                'border rounded-lg transition-all duration-300 overflow-hidden',
                !collapsedTickers.has(competitor.ticker)
                  ? 'bg-surface-light/50 border-purple-500/40'
                  : 'bg-surface/30 border-border/50 hover:border-purple-500/30'
              )}
            >
              {/* Header - Always Visible */}
              <button
                onClick={() => setCollapsedTickers(prev => {
                  const next = new Set(prev);
                  if (next.has(competitor.ticker)) {
                    next.delete(competitor.ticker);
                  } else {
                    next.add(competitor.ticker);
                  }
                  return next;
                })}
                className="w-full p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono font-bold text-purple-400 text-base">{competitor.ticker}</span>
                  <span className="text-text-primary text-sm">{competitor.companyName}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right whitespace-nowrap">
                    <span className="font-mono text-text-primary text-base font-semibold">{competitor.priceFormatted || 'N/A'}</span>
                    <span className={cn('ml-3 text-sm font-medium', (competitor.changePercent ?? 0) >= 0 ? 'text-green-400' : 'text-red-400')}>
                      {competitor.changeFormatted || ''}
                    </span>
                  </div>
                  <ChevronDown className={cn('w-4 h-4 text-text-muted transition-transform', !collapsedTickers.has(competitor.ticker) && 'rotate-180')} />
                </div>
              </button>

              {/* Expanded View */}
              {!collapsedTickers.has(competitor.ticker) && (
                <div className="px-3 pb-3 border-t border-border/50">
                  <div className={cn('grid gap-2 mt-3', isSmallScreen ? 'grid-cols-2' : 'grid-cols-5')}>
                    <CompetitorDataCell label="Market Cap" value={competitor.marketCapFormatted || 'N/A'} colorClass="text-emerald-400" />
                    <CompetitorDataCell label="P/E Ratio" value={competitor.peFormatted || 'N/A'} colorClass="text-emerald-400" />
                    <CompetitorDataCell label="EPS" value={competitor.epsFormatted || 'N/A'} colorClass="text-emerald-400" />
                    <CompetitorDataCell
                      label="Day Range"
                      value={competitor.dayLow && competitor.dayHigh ? `$${competitor.dayLow.toFixed(2)} - $${competitor.dayHigh.toFixed(2)}` : 'N/A'}
                      colorClass="text-amber-400"
                    />
                    <CompetitorDataCell label="52W Range" value={competitor['52WeekRange'] || 'N/A'} colorClass="text-amber-400" />
                  </div>
                  <div className={cn('grid gap-2 mt-2', isSmallScreen ? 'grid-cols-2' : 'grid-cols-4')}>
                    <CompetitorDataCell label="Volume" value={competitor.volume ? competitor.volume.toLocaleString() : 'N/A'} colorClass="text-emerald-400" />
                    <CompetitorDataCell label="Avg Volume" value={competitor.avgVolume ? competitor.avgVolume.toLocaleString() : 'N/A'} colorClass="text-emerald-400" />
                    <CompetitorDataCell label="Sector" value={competitor.sector || 'N/A'} colorClass="text-text-primary" />
                    <CompetitorDataCell label="Industry" value={competitor.industry || 'N/A'} colorClass="text-text-primary" />
                  </div>
                  {competitor.employees && (
                    <div className="mt-2 text-xs text-text-muted">
                      <span className="text-purple-400">Employees:</span> {competitor.employees.toLocaleString()}
                      {competitor.website && (
                        <>
                          {' | '}
                          <a href={competitor.website} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                            Website <ExternalLink className="w-3 h-3 inline" />
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fallback Data Sources (when no competitor data) */}
      {!hasCompetitors && isComplete && (
        <div className={cn('grid gap-2 text-sm mb-3', isSmallScreen ? 'grid-cols-2' : 'grid-cols-4')}>
          <DataCell label="Industry" value="Reports" highlight={isComplete} />
          <DataCell label="Market" value="Analysis" highlight={isComplete} />
          <DataCell label="Competitors" value="Pending" highlight={false} />
          <DataCell label="Trends" value="Forecast" highlight={isComplete} />
        </div>
      )}

      {/* Workstream Assignment */}
      {isComplete && workstreamCount > 0 && (
        <div className="text-xs text-text-muted border-t border-border pt-2">
          <span className="text-purple-400">Workstreams assigned:</span>{' '}
          <span className="text-emerald-400">{workstreamCount} parallel streams</span>
        </div>
      )}
    </div>
  );
}
